import { Api, APIResult } from 'utils/Api';
import { AccountClient, ClientOutput } from '../Clients/AccountClient';
import {
  call,
  select,
  put,
  takeLatest,
  takeEvery,
  all,
  CallEffect,
  PutEffect,
} from 'redux-saga/effects';
import { ApiGetter } from '../Clients/ApiGetter';
import {
  Type,
  actions,
  ProfileUpdateRequest,
  PhotoUpdateRequest,
  UpdatePasswordRequest,
  Generate2FARequest,
  Enable2FARequest,
  Disable2FARequest,
  UpdateUserStorageRequest,
  DisplayNameRequest,
} from '../../reducers/Account';
import { navigate } from '../../reducers/Navigation';
import { popDest } from '../../utils/intendedDest';
import * as Analytics from '../../utils/analytics';
import {
  confirmedRatingsKey,
  getAnonymousAction,
} from '../../utils/anonymousActionsStore';
import {
  tryRedirectToReviewerSignup,
  tryRedirectToVerificationPage,
} from '../signups';
import { tieUserToUnauthedRatings } from '../ratings';
import { tryLoginVerifyMissingUserRedirect } from '../login';
import { accountSelector, accountUserSelector } from '../../selectors/Account';
import { Account } from 'sagas/Clients/AccountClient/Models/Account';
import { pathnameSelector } from '../../selectors/Router';
import { User } from '../../models/User';
import {
  USER_LOGGED_IN,
  setLocalStorage,
  removeLocalStorage,
  getLocalStorage,
} from 'utils/storageHelpers';

export const accountClient = new AccountClient(new ApiGetter());

export function* getAccount(): any {
  const { data, error }: ClientOutput = yield call([
    accountClient,
    accountClient.retrieveAsync,
  ]);

  if (!data) {
    // call the /status API to make sure that we have a valid xsrf token set since the
    // account API didn't return a 200.
    yield call(Api.get, '/status');

    if (error) {
      // If the user was previously logged in, but now they're not,
      // shut down the analytics session. this could have happened from the session expiring
      // or the user logging out.
      if (error.status === 401) {
        const wasLoggedIn = yield call(getLocalStorage, USER_LOGGED_IN);
        if (wasLoggedIn) {
          yield call(removeLocalStorage, USER_LOGGED_IN);
          yield call(Analytics.shutdown);
        }
      }

      yield put(actions.setAccountError(error));
    }
    return;
  }

  yield put(actions.setAccount(data));

  yield call(setLocalStorage, USER_LOGGED_IN, true);

  const user = data.user;

  const confirmedRatingUuids = getAnonymousAction(confirmedRatingsKey);
  if (confirmedRatingUuids && confirmedRatingUuids.length) {
    yield tieUserToUnauthedRatings();
  }

  // Always initialize on successful login. Segment says you only need to call this
  // once unless one of these values changes, but then other integrations like Intercom
  // require it to be called on every page load to identify the user and load the widget.
  Analytics.initialize(
    user.uuid,
    user.email,
    user.name,
    user.intercom_hash,
    user.is_reviewer,
    user.is_developer,
    user.is_benchmarks_signup
  );
}

export function* navigateAfterAccountFetch() {
  const account: Account = yield select(accountSelector);
  const { user } = account;
  const pathname: string = yield select(pathnameSelector);

  // unauthed user
  if (!user) {
    // No twoFactorChallengeToken to verify 2FA
    const isRedirectedToLoginPage: boolean = yield call(
      tryLoginVerifyMissingUserRedirect,
      pathname
    );
    if (isRedirectedToLoginPage) {
      return;
    }
    return;
  }

  // unverified users should always get sent to the email verification screen
  const isRedirectedToVerificationPage: boolean = yield call(
    tryRedirectToVerificationPage,
    pathname
  );
  if (isRedirectedToVerificationPage) {
    return;
  }

  // authed user is a reviewer
  const isRedirectedToReviewerSignup: boolean = yield call(
    tryRedirectToReviewerSignup,
    pathname
  );
  if (isRedirectedToReviewerSignup) {
    return;
  }

  if (!user.terms_accepted) {
    return;
  }

  // Navigate to intendedDest if it exists
  const intendedDest: string | null = yield call(popDest);
  if (intendedDest) {
    yield put(navigate({ href: intendedDest }));
  }

  yield call(tryRedirectToDash, pathname);
}

export function* tryRedirectToDash(pathname: string) {
  const user: User = yield select(accountUserSelector);

  const isLoginRoute = pathname === '/login';
  const isLoginVerifyRoute = pathname === '/login/verify';
  const isEmailVerificationStepRoute = pathname === '/verification';
  const isCompleteDeveloperProfileRoute = pathname === '/complete_profile';
  // Navigate to dash based on route
  if (
    isLoginRoute ||
    isLoginVerifyRoute ||
    (isEmailVerificationStepRoute && user.email_verified) ||
    (isCompleteDeveloperProfileRoute && user.terms_accepted)
  ) {
    yield put(navigate({ href: '/dash' }));
  }
}

export function* updateProfileInfo(
  action: ProfileUpdateRequest
): Generator<CallEffect | PutEffect> {
  const { name, company, company_url, terms_accepted, callback } = action;
  if (!name) {
    throw new Error('Missing required parameters for updating profile info');
  }
  const response: APIResult = yield call(Api.put, 'account/profile', {
    name,
    company,
    company_url,
    terms_accepted,
  });
  if (!response.success) {
    yield put({
      type: Type.PROFILE_UPDATE_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: Type.PROFILE_UPDATE_SUCCESS,
    });
    yield call(getAccount);
    if (callback) {
      yield call(callback);
    }
  }
}

export function* updatePhoto(
  action: PhotoUpdateRequest
): Generator<CallEffect> {
  const { image, callback } = action;
  if (!image) {
    throw new Error('Missing required parameters for updating photo');
  }
  const response: APIResult = yield call(
    Api.upload,
    'account/photo',
    'photo',
    image
  );
  yield call(getAccount);
  if (callback) {
    yield call(callback, response.success);
  }
}

export function* updatePassword(
  action: UpdatePasswordRequest
): Generator<CallEffect> {
  const { oldPassword, newPassword, callback } = action;
  if (!oldPassword || !newPassword) {
    throw new Error('Missing required parameters for updating password');
  }
  const response: APIResult = yield call(Api.put, 'account/password', {
    old_password: oldPassword,
    new_password: newPassword,
    new_password_confirmation: newPassword,
  });
  if (callback) {
    const errorMessage =
      response.data && response.data.error_message
        ? response.data.error_message
        : 'Unable to set password';
    yield call(
      callback,
      response.success,
      response.success ? null : errorMessage
    );
  }
}

export function* generate2faCode(
  action: Generate2FARequest
): Generator<CallEffect> {
  const { callback } = action;
  const response: APIResult = yield call(
    Api.get,
    'account/two-factor/generate'
  );
  if (callback) {
    const data = response.data ? response.data : {};
    const errorMessage = data.error_message
      ? data.error_message
      : 'Unable to generate 2fa code';
    yield call(callback, data.qr_code, response.success ? null : errorMessage);
  }
}

export function* enable2fa(action: Enable2FARequest): Generator<CallEffect> {
  const { code, callback } = action;
  if (!code) {
    throw new Error('Missing required parameters for enabling 2fa');
  }
  const response: APIResult = yield call(
    Api.post,
    'account/two-factor/enable',
    { code }
  );
  if (response.success) {
    yield call(getAccount);
  }
  if (callback) {
    const data = response.data ? response.data : {};
    const errorMessage = data.error_message
      ? data.error_message
      : 'Unable to enable 2fa';
    yield call(
      callback,
      data.recovery_code,
      response.success ? null : errorMessage
    );
  }
}

export function* disable2fa(
  action: Disable2FARequest
): Generator<CallEffect | PutEffect> {
  const { confirmationPassword, callback } = action;
  const response: APIResult = yield call(
    Api.del,
    'account/two-factor/disable',
    {
      verification_password: confirmationPassword,
    },
    true
  );
  if (response.success) {
    yield call(getAccount);
    yield put({ type: Type.DISABLE_2FA_SUCCESS });
    if (callback) {
      yield call(callback);
    }
  } else if (response.status === 429) {
    yield put({
      type: Type.DISABLE_2FA_FAILURE,
      error:
        'You have exceeded the amount of password confirmation attempts. Please wait a few minutes and try again.',
    });
  } else {
    yield put({
      type: Type.DISABLE_2FA_FAILURE,
      error: 'Invalid password.',
    });
  }
}

export function* resendEmail(): Generator<CallEffect | PutEffect> {
  const response: APIResult = yield call(
    Api.post,
    'account/resend-email-verification'
  );
  yield put({
    type: response.success
      ? Type.RESEND_EMAIL_SUCCESS
      : Type.RESEND_EMAIL_FAILURE,
  });
}

export function* updateUserStorage(
  action: UpdateUserStorageRequest
): Generator<CallEffect | PutEffect> {
  const { keyValuePair } = action;
  if (!keyValuePair) {
    throw new Error('Missing required parameters for updating user storage');
  }
  const response: APIResult = yield call(Api.put, 'user/storage', keyValuePair);
  const data = response.data;
  if (!response.success) {
    yield put({
      type: Type.STORAGE_UPDATE_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: Type.STORAGE_UPDATE_SUCCESS,
      keyValuePair,
    });
    yield call(getAccount);
  }
}

export function* requestDisplayNameChange(
  action: DisplayNameRequest
): Generator<CallEffect | PutEffect> {
  const { display_name } = action;
  if (!display_name) {
    throw new Error(
      'Missing required parameters for request display name change'
    );
  }
  const response: APIResult = yield call(
    Api.post,
    'account/change-display-name',
    {
      display_name,
    }
  );
  const data = response.data;
  if (!response.success) {
    yield put({
      type: Type.DISPLAY_NAME_FAILURE,
      error: data ? data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: Type.DISPLAY_NAME_SUCCESS,
    });
    yield call(getAccount);
  }
}

export function* accountSaga() {
  yield takeLatest(Type.FETCH_ACCOUNT_REQUEST, getAccount);
  yield takeEvery(
    [Type.FETCH_ACCOUNT_SUCCESS, Type.FETCH_ACCOUNT_FAILURE],
    navigateAfterAccountFetch
  );
  yield all([
    takeEvery(Type.DISABLE_2FA_REQUEST, disable2fa),
    takeEvery(Type.ENABLE_2FA_REQUEST, enable2fa),
    takeEvery(Type.GENERATE_2FA_REQUEST, generate2faCode),
    takeEvery(Type.RESEND_EMAIL_REQUEST, resendEmail),
    takeEvery(Type.PASSWORD_UPDATE_REQUEST, updatePassword),
    takeEvery(Type.PHOTO_UPDATE_REQUEST, updatePhoto),
    takeEvery(Type.PROFILE_UPDATE_REQUEST, updateProfileInfo),
    takeEvery(Type.STORAGE_UPDATE_REQUEST, updateUserStorage),
    takeEvery(Type.DISPLAY_NAME_REQUEST, requestDisplayNameChange),
  ]);
}
